import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, User, ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ withCredentials: true });

const fmt = (n) =>
  typeof n === 'number' ? `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
};

function AmendmentCard({ entry, idx }) {
  const [open, setOpen] = useState(false);
  const lines = entry.lines_changed || [];
  return (
    <div
      className="border rounded-xl overflow-hidden"
      style={{ borderColor: '#fde68a', background: '#fffbeb' }}
      data-testid={`amendment-entry-${idx}`}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-yellow-50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-amber-900">{entry.amended_by_label || 'Unknown'}</span>
            <span className="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
              {fmt(entry.amended_value)}
            </span>
            <span className="text-xs text-gray-500">{fmtDate(entry.amended_at)}</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {entry.lines_changed_count === 0
              ? 'No line changes detected'
              : `${entry.lines_changed_count} line${entry.lines_changed_count !== 1 ? 's' : ''} changed`}
          </p>
        </div>
        {lines.length > 0 && (
          open ? <ChevronDown className="w-4 h-4 text-amber-600 shrink-0" /> : <ChevronRight className="w-4 h-4 text-amber-600 shrink-0" />
        )}
      </button>
      {open && lines.length > 0 && (
        <div className="px-4 pb-3 space-y-1.5 border-t" style={{ borderColor: '#fde68a' }}>
          {lines.map((ln, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span
                className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold"
                style={ln.is_new
                  ? { background: '#d1fae5', color: '#065f46' }
                  : { background: '#fef3c7', color: '#92400e' }}
              >{ln.is_new ? 'NEW' : 'EDIT'}</span>
              <span className="text-gray-700 truncate flex-1">{ln.description || `Line ${ln.index + 1}`}</span>
              <span className="font-mono text-amber-800 shrink-0">{fmt(ln.new_value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function QuoteVersionHistoryModal({ quoteId, customerName, quoteNumber, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!quoteId) return;
    setLoading(true);
    setError('');
    api.get(`${API}/api/quotes/${quoteId}/amendments`)
      .then(r => setData(r.data))
      .catch(() => setError('Could not load amendment history.'))
      .finally(() => setLoading(false));
  }, [quoteId]);

  const history = (data?.amendment_history || []).slice().reverse(); // newest first

  const modal = (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="quote-version-history-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-900 text-base">Version History</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {customerName} {quoteNumber ? `· ${quoteNumber}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="version-history-close-btn"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-8 text-gray-400 text-sm gap-2">
              <Clock className="w-4 h-4 animate-spin" /> Loading history…
            </div>
          )}
          {error && !loading && (
            <div className="text-sm text-red-500 text-center py-8">{error}</div>
          )}
          {!loading && !error && history.length === 0 && (
            <div className="text-sm text-gray-400 text-center py-8">
              No amendment history found for this quote.
            </div>
          )}
          {!loading && !error && history.map((entry, idx) => (
            <AmendmentCard key={idx} entry={entry} idx={idx} />
          ))}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
